import {
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    Image,
    Modal,
    View
} from "react-native";
import type ThreeTParticipantsSideBarProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import type {
    ThreeTParticipantDTO
} from "../../../../services/restAPI/actions/threeTGetParticipantsForAdmin/type";
import type {
    AddParticipantSheetRef
} from "../addParticipantSheet/type";
import {
    CheckCircle2Icon,
    CircleIcon,
    XIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import AddParticipantSheet from "../addParticipantSheet";

const ParticipantsSideBar = ({
    setSelectedParticipants,
    selectedParticipants,
    isSelectionDisabled,
    participants,
    eventSubID,
    setVisible,
    onRefresh,
    eventID,
    visible
}: ThreeTParticipantsSideBarProps & { onRefresh?: () => void, eventID?: string | null, eventSubID?: string | null }) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const addSheetRef = useRef<AddParticipantSheetRef>(null);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const handleSelect = (participant: ThreeTParticipantDTO) => {
        if (isSelectionDisabled) {
            return;
        }

        let newSelection;
        if (selectedParticipants.find(p => p.id === participant.id)) {
            newSelection = selectedParticipants.filter(p => p.id !== participant.id);
        } else {
            newSelection = [
                ...selectedParticipants,
                participant
            ];
        }
        setSelectedParticipants(newSelection);

        if (eventSubID) {
            AsyncStorage.setItem(`@ThreeT_SelectedParticipants_${eventSubID}`, JSON.stringify(newSelection.map(p => p.id))).catch(() => {});
        }
    };

    const filteredParticipants = participants.filter(p => {
        const fullName = p.name ? p.name : (p.userInfo?.name ? `${p.userInfo.name} ${p.userInfo.surname || ""}` : "");
        const companyName = p.companyName || "";
        return fullName.toLowerCase().includes(searchQuery.toLowerCase()) || companyName.toLowerCase().includes(searchQuery.toLowerCase());
    });

    return <Modal
        onRequestClose={() => setVisible(false)}
        animationType="slide"
        transparent={true}
        visible={visible}
    >
        <View
            style={[
                stylesheet.modalOverlay,
                {
                    backgroundColor: colors.content.container.overlay
                }
            ]}
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.project.brandWhite,
                        width: isMobile ? "100%" : 400,
                        padding: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.modalHeader,
                        {
                            marginBottom: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        style={{
                            marginRight: spaces.spacingMd,
                            flex: 1
                        }}
                        variant="titleMediumSize"
                    >
                        {localize("threet-participants-header")} ({selectedParticipants.length}/{participants.length})
                    </Text>
                    <View
                        style={{
                            flexDirection: "row",
                            gap: spaces.spacingSm
                        }}
                    >
                        <Button
                            onPress={() => {
                                if (selectedParticipants.length === participants.length) {
                                    setSelectedParticipants([]);
                                    if (eventSubID) {
                                        AsyncStorage.removeItem(`@ThreeT_SelectedParticipants_${eventSubID}`).catch(() => {});
                                    }
                                } else {
                                    setSelectedParticipants([
                                        ...participants
                                    ]);
                                    if (eventSubID) {
                                        AsyncStorage.setItem(`@ThreeT_SelectedParticipants_${eventSubID}`, JSON.stringify(participants.map(p => p.id))).catch(() => {});
                                    }
                                }
                            }}
                            title={selectedParticipants.length > 0 && selectedParticipants.length === participants.length ? "Tümünü Temizle" : "Tümünü Seç"}
                            isDisabled={isSelectionDisabled || participants.length === 0}
                            customBorderColor="secondaryButton"
                            customColor="secondaryButton"
                            variant="filled"
                            size="small"
                        />
                        <Button
                            onPress={() => addSheetRef.current?.open()}
                            title={localize("threet-add-member")}
                            customBorderColor="secondaryButton"
                            isDisabled={isSelectionDisabled}
                            customColor="secondaryButton"
                            variant="filled"
                            size="small"
                        />
                        <TouchableOpacity
                            onPress={() => setVisible(false)}
                            style={{
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            <XIcon
                                color={colors.content.icon.high}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                </View>
                <View
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    <TextInput
                        placeholder={localize("threet-search-participants") || "Katılımcı Ara..."}
                        customColor={colors.project.brandWhite}
                        onChangeText={setSearchQuery}
                        spreadBehaviour="stretch"
                        value={searchQuery}
                    />
                </View>
                <ScrollView>
                    {filteredParticipants.map(participant => {
                        const isSelected = selectedParticipants.some(p => p.id === participant.id);

                        return <TouchableOpacity
                            style={[
                                stylesheet.participantButton,
                                {
                                    backgroundColor: isSelected ? colors.project.gold + "20" : "transparent",
                                    marginBottom: spaces.spacingSm,
                                    borderRadius: radiuses.sm,
                                    padding: spaces.spacingSm,
                                    gap: spaces.spacingSm
                                }
                            ]}
                            onPress={() => handleSelect(participant)}
                            disabled={isSelectionDisabled}
                            key={participant.id}
                        >
                            {
                                isSelected ?
                                    <CheckCircle2Icon
                                        color={colors.content.icon.success}
                                        size={20}
                                    />
                                :
                                    <CircleIcon
                                        color={colors.content.icon.default}
                                        size={20}
                                    />
                            }
                            {
                                participant.logo ?
                                    <Image
                                        style={[
                                            stylesheet.avatar,
                                            {
                                                borderRadius: radiuses.full
                                            }
                                        ]}
                                        source={{
                                            uri: participant.logo
                                        }}
                                    />
                                :
                                    <View
                                        style={[
                                            stylesheet.avatarPlaceholder,
                                            {
                                                backgroundColor: colors.content.border.subtle,
                                                borderRadius: radiuses.full,
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }
                                        ]}
                                    >
                                        <Text
                                            variant="bodySmallSize"
                                        >
                                            {
                                                (participant.name || participant.userInfo?.name || "U")
                                                    .substring(0, 1)
                                                    .toUpperCase()
                                            }{
                                                (participant.userInfo?.surname || participant.name?.split(" ")[1] || "")
                                                    .substring(0, 1)
                                                    .toUpperCase()
                                            }
                                        </Text>
                                    </View>
                            }
                            <View
                                style={stylesheet.participantInfo}
                            >
                                <Text
                                    variant="bodyMediumSize"
                                    color="high"
                                >
                                    {participant.name ? participant.name : (participant.userInfo?.name ? `${participant.userInfo.name} ${participant.userInfo.surname || ""}` : localize("threet-anonymous-participant"))}
                                    {
                                        !participant.formAnswers && (
                                            <Text
                                                variant="bodySmallSize"
                                                color="warning"
                                            >
                                                {localize("threet-manual-add-suffix")}
                                            </Text>)
                                    }
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {participant.companyName || localize("threet-no-company-info")}
                                </Text>
                            </View>
                        </TouchableOpacity>;
                    })}
                </ScrollView>
            </View>
        </View>
        <AddParticipantSheet
            onParticipantAdded={() => onRefresh && onRefresh()}
            eventSubID={eventSubID || null}
            participants={participants}
            eventID={eventID || null}
            ref={addSheetRef}
        />
    </Modal>;
};

export default ParticipantsSideBar;
